import {
  ArrowRightOutlined,
  DeleteOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Form, Input } from 'antd';
import type { RangeValue } from 'rc-picker/es/interface';
import React, { useContext, useMemo } from 'react';

import { RangePicker } from 'app/components/fields/dayjsPickers';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { format, toDayjs } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';

import { FormLineLayout } from '../Form/FormLineLayout/FormLineLayout';

import styles from './Disclaimer.module.scss';

const TextDisplay = ({ value }: { value?: string }) => <span>{value}</span>;

type Props = {
  listName: string;
  fieldName: number;
  sortableId: number | string;
  onRemove: () => void;
};

export const DisclaimerBloc = ({
  listName,
  fieldName,
  sortableId,
  onRemove,
}: Props) => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dateRange = Form.useWatch([listName, fieldName, 'dateRange'], form);

  const rangePickerValue = useMemo<RangeValue<Dayjs>>(() => {
    if (!dateRange) {
      return [null, null];
    }

    if (Array.isArray(dateRange)) {
      return [toDayjs(dateRange[0]), toDayjs(dateRange[1])];
    }

    return [null, null];
  }, [dateRange]);

  return (
    <div className="disclaimer-bloc" ref={setNodeRef} style={style}>
      <div className="disclaimer-left">
        <FormLineLayout label="Message" labelOnTop>
          <Form.Item name={[fieldName, 'text']} noStyle>
            {isEditMode ? (
              <Input.TextArea
                id={`${listName}-${fieldName}-textarea`}
                rows={3}
              />
            ) : (
              <TextDisplay />
            )}
          </Form.Item>
        </FormLineLayout>

        <div className="spacer" />

        <FormLineLayout label="Date range">
          <Form.Item name={[fieldName, 'dateRange']} noStyle>
            {isEditMode ? (
              <RangePicker
                format="DD-MM-YYYY"
                allowEmpty={[false, true]}
                getPopupContainer={(trigger) =>
                  trigger.parentElement ?? trigger
                }
              />
            ) : (
              <span className={styles.dateRangeDisplay}>
                {rangePickerValue?.[0]
                  ? format(rangePickerValue[0], 'DD-MM-YYYY')
                  : '-'}
                <ArrowRightOutlined />
                {rangePickerValue?.[1]
                  ? format(rangePickerValue[1], 'DD-MM-YYYY')
                  : '-'}
              </span>
            )}
          </Form.Item>
        </FormLineLayout>
      </div>

      {isEditMode && (
        <div className="disclaimer-right">
          <DeleteOutlined onClick={onRemove} />
          <MenuOutlined
            className="sortable-handle"
            {...attributes}
            {...listeners}
          />
        </div>
      )}
    </div>
  );
};
